
import { textSearch } from "@/assets/ts/_utils";
import JsonExcel from "@/components/widgets/tables/JsonExcel.vue";
import { useAdminComarca } from "@/composable/useAdminComarca";
import { edadesProfesionales } from "@/core/data/edadesProfesionales";
import { generos } from "@/core/data/generos";
import { municipiosComarcas } from "@/core/data/municipiosProvinciasComarcas";
import { titulacionesTodas } from "@/core/data/titulaciones";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { SortProps, sortFn } from "@/core/helpers/sort";
import { ProfesionalEstado } from "@/enums";
import Page from "@/layout/Page.vue";
import {
  AuthModule,
  OrganismosModule,
  ProfesionalesListadoModule,
} from "@/store/modules";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { ErrorMessage, useField } from "vee-validate";
import {
  computed,
  defineComponent,
  onActivated,
  onBeforeMount,
  onMounted,
  ref,
  watch,
} from "vue";
import { useArrayPagination } from "vue-composable";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { getModule } from "vuex-module-decorators";

export default defineComponent({
  name: "profesionales-listado",
  components: { "download-excel": JsonExcel, Page, ErrorMessage },
  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const store = useStore();
    const module = getModule(ProfesionalesListadoModule, store);
    const authModule = getModule(AuthModule, store);
    const comarcaId = authModule.user.comarcaId;
    const isAdmin = !comarcaId;

    const organismosModule = getModule(OrganismosModule, store);
    const { selectedComarcaId } = useAdminComarca();
    const comarcaSelectKey = ref(0);
    const loadingComarcas = ref(false);

    const ensureComarcasLoaded = async () => {
      if (organismosModule.organismosListado.length === 0) {
        loadingComarcas.value = true;
        try {
          await organismosModule.fetchAll();
        } finally {
          loadingComarcas.value = false;
        }
      }
    };

    const comarcas = computed(() => {
      const list = organismosModule.organismosListado.map((item) => ({
        id: item.id,
        nombre: item.nombre,
      }));
      list.unshift({ id: "TODAS", nombre: "Todas" });
      return list;
    });

    const csvLabels = {
      nombreComarca: t("csv.solicitudes.familias.comarca"),
      nombre: t("csv.listados.profesionales.nombre"),
      apellidos: t("csv.listados.profesionales.apellidos"),
      dni: t("csv.listados.profesionales.dni"),
      email: t("csv.listados.profesionales.email"),
      telefono: t("csv.listados.profesionales.telefono"),
      fechaNacimientoCsv: t("csv.listados.profesionales.fechaNacimiento"),
      direccion: t("csv.listados.profesionales.direccion"),
      localidad: t("csv.listados.profesionales.localidad"),
      provincia: t("csv.listados.profesionales.provincia"),
      titulacionText: t("csv.solicitudes.profesionales.titulacion"),
      titulacionOtras: t("csv.solicitudes.profesionales.titulacionOtras"),
      carnetConducir: t("csv.listados.profesionales.carnetConducir"),
      vehiculo: t("csv.listados.profesionales.vehiculo"),
    };
    const csvFields = [
      ...(!comarcaId ? ["nombreComarca"] : []),
      "nombre",
      "apellidos",
      "dni",
      "email",
      "telefono",
      "fechaNacimientoCsv",
      "direccion",
      "localidad",
      "provincia",
      "titulacionText",
      "titulacionOtras",
      "carnetConducir",
      "vehiculo",
    ];
    const csvName = "profesionalesListado" + new Date().getTime() + ".xlsx";

    const search = ref("");

    const sortNombreApellidos = function (a, b) {
      let primero = a.nombre + " " + a.apellidos;
      let segundo = b.nombre + " " + b.apellidos;
      if (primero > segundo) {
        return 1;
      }
      if (primero < segundo) {
        return -1;
      }
      return 0;
    };
    const cargaListado = ref(false);

    onMounted(() => {
      setCurrentPageBreadcrumbs(t("menu.profesionales"), [t("menu.personas")]);
    });
    const handleEdit = (index, row) => {
      router.push(`profesionales/${row.id}`);
    };

    const profesionales = computed(() => {
      return module.profesionalesListado?.map((ele) => {
        // calcular edad de manera robusta
        let edad: number | null = null;
        if (ele?.fechaNacimiento) {
          const dob = new Date(ele.fechaNacimiento);
          if (!isNaN(dob.getTime())) {
            const today = new Date();
            edad = today.getFullYear() - dob.getFullYear();
            const m = today.getMonth() - dob.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
              edad--;
            }
          }
        }

        return {
          ...ele,
          fechaNacimientoCsv: new Date(ele.fechaNacimiento),
          edad,
          titulacion: ele.titulacion?.map((titulacion) => {
            return (
              titulacionesTodas.find((ele) => ele.id === titulacion)?.text ??
              titulacion
            );
          }),
          nombreCompleto: [ele.nombre, ele.apellidos].join(" "),
          titulacionText:
            ele.titulacion
              ?.map((t) => titulacionesTodas.filter((a) => a.id == t))
              ?.map((t) => t.map((r) => r.text))
              .join(", ") || "",
          titulacionOtras: ele.titulacionOtras,
          nombreComarca: ele.comarca?.nombre,
        };
      });
    });

    const sortProps = ref<SortProps>({
      prop: "createdOn",
      order: "descending",
      method: null,
    });

    const filteredCollection = computed(() =>
      profesionales.value
        .slice()
        .sort(sortFn(sortProps))
        .filter((data) => {
          return (
            !search.value ||
            textSearch(!comarcaId ? data.nombreComarca : "", search.value) ||
            textSearch(data.nombreCompleto, search.value) ||
            textSearch(data.dni, search.value) ||
            textSearch(data.email, search.value) ||
            textSearch(data.telefono, search.value) ||
            textSearch(data.localidad, search.value)
          );
        })
    );

    const { value: localidad } = useField("localidad");
    const { value: genero } = useField("genero");
    const { value: rangoedad } = useField("rangoedad");

    const profesionalesListado = computed(() => {
      let filteredBySearch = (profesionales.value ?? [])
        .slice()
        .sort(sortFn(sortProps))
        .filter((data) => {
          return (
            !search.value ||
            textSearch(!comarcaId ? data.nombreComarca : "", search.value) ||
            textSearch(data.nombreCompleto, search.value) ||
            textSearch(data.dni, search.value) ||
            textSearch(data.email, search.value) ||
            textSearch(data.telefono, search.value) ||
            textSearch(data.localidad, search.value)
          );
        });

      if (localidad?.value !== undefined && localidad?.value !== "") {
        // Intersección de registros filtrados por búsqueda y por localidad
        filteredBySearch = filteredBySearch?.filter((data) =>
          profesionales.value
            ?.filter((data) => data.localidad == localidad.value)
            ?.includes(data)
        );
      }
      if (genero?.value !== undefined && genero?.value !== "") {
        filteredBySearch = filteredBySearch?.filter((data) =>
          profesionales.value
            ?.filter((data) => data.tratamiento == genero.value)
            ?.includes(data)
        );
      }

      if (rangoedad?.value !== undefined && rangoedad?.value !== "") {
        filteredBySearch = filteredBySearch?.filter((data) => {
          const edadActual = data.edad;
          // si no hay fecha válida, no incluir en filtro por edad
          if (edadActual === null || edadActual === undefined) return false;

          if (rangoedad.value === "MENOS30") {
            return edadActual < 30;
          } else if (rangoedad.value === "3045") {
            return edadActual >= 30 && edadActual <= 45;
          } else if (rangoedad.value === "MAS45") {
            return edadActual > 45;
          }
          return true;
        });
      }

      return filteredBySearch;
    });

    const municipiosComarca = computed(() => {
      if (!isAdmin) {
        return municipiosComarcas.filter(
          (municipio) => municipio.comarcaId === comarcaId
        );
      }

      if (!selectedComarcaId.value || selectedComarcaId.value === "TODAS") {
        return municipiosComarcas;
      }

      return municipiosComarcas.filter(
        (municipio) => municipio.comarcaId === selectedComarcaId.value
      );
    });

    const { result, currentPage, lastPage } = useArrayPagination(
      profesionalesListado,
      { pageSize: 100 }
    );

    const fetchData = async () => {
      if (isAdmin && !selectedComarcaId.value) {
        module.setData([]);
        cargaListado.value = false;
        search.value = "";
        currentPage.value = 1;
        return;
      }

      let idToFetch: string | undefined;

      if (isAdmin) {
        idToFetch =
          selectedComarcaId.value === "TODAS"
            ? undefined
            : selectedComarcaId.value!;
      } else {
        idToFetch = comarcaId;
      }

      cargaListado.value = true;
      try {
        await module.fetchProfesionales(idToFetch);
        search.value = "";
        currentPage.value = 1;
      } catch (err) {
        Swal.fire({
          text: t("formularios.profesionalesListado.cargar.error"),
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: t("dialogs.okButton"),
          customClass: {
            confirmButton: "btn btn-primary",
          },
        });
      } finally {
        cargaListado.value = false;
      }
    };

    if (isAdmin) {
      watch(selectedComarcaId, async () => {
        localidad.value = "";
        genero.value = "";
        rangoedad.value = "";
        await fetchData();
      });
    }

    onBeforeMount(async () => {
      if (isAdmin) {
        ensureComarcasLoaded();
      }
      await fetchData();
    });
    onActivated(async () => {
      if (isAdmin) {
        ensureComarcasLoaded();
        comarcaSelectKey.value++;
      }
      await fetchData();
    });

    const onComarcaVisibleChange = (open: boolean) => {
      if (open && isAdmin) {
        ensureComarcasLoaded();
      }
    };

    return {
      profesionalesListado,
      filteredCollection,
      cargaListado,
      isAdmin,
      csvLabels,
      csvFields,
      csvName,
      handleEdit,
      ProfesionalEstado,
      sortNombreApellidos,
      search,
      sortChange: ({ column, prop, order }) => {
        sortProps.value = { prop, order, method: column.sortMethod };
      },
      result,
      currentPage,
      lastPage,
      handleCurrentPageChange: (page: number) => (currentPage.value = page),
      municipiosComarca,
      localidad,
      generos,
      genero,
      rangoedad,
      edadesProfesionales,
      selectedComarcaId,
      comarcas,
      loadingComarcas,
      onComarcaVisibleChange,
      comarcaSelectKey,
    };
  },
});
